<template>
    <v-layout justify-center>
        <v-responsive max-width="1240" width="100%" class="px-3 mx-n3">
            <list-heading title="무통장입금 거래 목록" showsFilterButton v-model="showsSearch" />

            <account-order-list-search v-bind="{ showsSearch }" />
            <account-order-list-filter class="mb-3" />

            <v-data-table v-bind="{ items, headers, loading }" :items-per-page="-1" disable-pagination disable-sort hide-default-footer class="v-sheet--outlined">
                <template v-for="header in headers.filter((header) => header.hasOwnProperty('formatter'))" #[`item.${header.value}`]="{ value }"> {{ header.formatter(value) }} </template>

                <template #[`item.state`]="{ item, value }">
                    <account-order-state-edit :value="item" v-on="{ update }">
                        <v-btn text tile block class="caption justify-start">{{ FORM_STATES__POINT_ORDER_ACCOUNT[value]?.text }}</v-btn>
                    </account-order-state-edit>
                </template>

                <template #[`item.point`]="{ item }">
                    <account-order-point-btn :value="item" v-on="{ update }" />
                </template>

                <template #[`item.memo`]="{ item, value }">
                    <form-memo-edit :value="item" v-on="{ update }">
                        <v-btn text tile block class="caption memo-area">{{ value || "-" }}</v-btn>
                    </form-memo-edit>
                </template>
            </v-data-table>

            <v-pagination :value="page" :length="pageCount" :total-visible="11" color="primary" class="my-2" @input="(page) => $router.push({ query: { ...$route.query, page } })" />
        </v-responsive>
    </v-layout>
</template>

<script>
import api from "@/api";

import VDateField from "@/components/plugins/vuetify/v-date-field.vue";
import ListHeading from "@/components/console/dumb/list-heading.vue";
import FormMemoEdit from "@/components/console/forms/form-memo-edit.vue";
import AccountOrderPointBtn from "@/components/console/forms/account-order-point-btn.vue";
import AccountOrderStateEdit from "@/components/console/forms/account-order-state-edit.vue";
import AccountOrderListSearch from "@/components/console/forms/account-order-list-search.vue";
import AccountOrderListFilter from "@/components/console/forms/account-order-list-filter.vue";
import { FORM_STATES__POINT_ORDER_ACCOUNT } from "@/assets/variables";

const code = "point-order-account";

const headers = [
    { width: 100, text: "요청일시", value: "createdAt", formatter: (value) => value?.toDateTime?.() },
    { width: 140, text: "사용자", value: "writer", formatter: ({ username, name, phone } = {}) => [username, name, phone].filter((item) => item).join("\n") },
    { width: 100, text: "입금자명", value: "meta.sender" },
    { width: 100, text: "입금액", value: "meta.amount", formatter: (value) => value?.format?.() || value || "-" },
    { width: 160, text: "현금영수증\n신청정보", value: "meta.cashReceipt", formatter: ({ enabled, content } = {}) => [enabled ? "신청" : "미신청", content].filter((item) => item).join(" / ") },
    { width: +90, text: "처리상태", value: "state", cellClass: "px-0" },
    { width: 220, text: "포인트\n지급하기", value: "point", cellClass: "px-0" },
    { width: 300, text: "메모", value: "memo", cellClass: "px-0" },
].map((item, index, array) => ({ ...item, formatter: item.formatter ?? ((value) => value ?? "-"), divider: index != array.length - 1, class: (item.class || "") + " white-space-pre-line", cellClass: (item.cellClass || "") + " caption line-height-1-5 white-space-pre-line py-2" }));

export default {
    components: {
        VDateField,
        ListHeading,
        FormMemoEdit,
        AccountOrderPointBtn,
        AccountOrderStateEdit,
        AccountOrderListSearch,
        AccountOrderListFilter,
    },
    data: () => ({
        forms: [],

        limit: 10,
        summary: { totalCount: 0 },

        headers,

        loading: false,
        showsSearch: true,
        FORM_STATES__POINT_ORDER_ACCOUNT,
    }),
    computed: {
        items() {
            return [...this.forms];
        },
        page() {
            return +(this.$route.query.page || "1");
        },
        skip() {
            return (this.page - 1) * this.limit;
        },
        pageCount() {
            return Math.ceil(this.summary.totalCount / this.limit) || 1;
        },
        params() {
            let { ...query } = this.$route.query;

            query.code = code;

            return { ...query };
        },
    },
    mounted() {
        this.init();
    },
    watch: {
        params() {
            this.search();
        },
    },
    methods: {
        async init() {
            this.forms = [];
            this.summary = { totalCount: 0 };

            await this.search();
        },

        async search() {
            if (this.loading) return;
            else this.loading = true;

            try {
                let { skip, limit, params } = this;
                var { summary, forms } = await api.console.forms.gets({
                    headers: { skip, limit },
                    params,
                });

                this.summary = summary;
                this.forms = forms;
            } finally {
                this.loading = false;
            }
        },

        async update(form) {
            if (this.loading) return;
            else this.loading = true;

            try {
                form = (await api.console.forms.put(form))?.form;
                alert("저장되었습니다");
                this.updateItem(form);
            } finally {
                this.loading = false;
                this.search();
            }
        },

        updateItem(form) {
            const index = this.forms.findIndex(({ _id }) => _id == form._id);
            if (0 <= index) this.forms.splice(index, 1, form);
            else {
                this.forms = [form, ...this.forms];
                this.summary.totalCount += 1;
            }
            this.search();
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep {
    .line-height-1-5 {
        line-height: 1.5;
    }
    .max-width-0 {
        max-width: 0;
    }
    .white-space-pre-line {
        white-space: pre-line;
    }
    .cursor-pointer {
        cursor: pointer;
    }
    .v-pagination button {
        box-shadow: none !important;
        border: thin solid rgba(0, 0, 0, 0.12);
    }
    .v-small-dialog__activator__content {
        width: 100%;
    }

    .memo-area {
        height: auto !important;

        .v-btn__content {
            display: block;
            text-align: initial;
            white-space: pre-line;
            text-transform: initial;
        }
    }
}
</style>
